import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Link } from "react-router-dom";
import { Search, RotateCcw, XCircle, Eye, ArrowUpRight, RefreshCw, Download, AlertTriangle } from "lucide-react";

const STATUS_CHIPS = {
  pending: "bg-yellow-100 text-yellow-800",
  sent: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-700",
  skipped_purchased: "bg-blue-100 text-blue-800",
  skipped_suppressed: "bg-amber-100 text-amber-900",
  skipped_recovered: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-slate-100 text-slate-700",
  mock: "bg-gray-100 text-gray-700",
};

const STATUS_LABEL = {
  pending: "قيد الانتظار", sent: "أُرسلت", failed: "فشلت",
  skipped_purchased: "اشترى بالفعل", skipped_suppressed: "قائمة إيقاف",
  skipped_recovered: "تعافى", cancelled: "أُلغيت", mock: "تجريبي",
};

export default function AdminEmailQueue() {
  const [health, setHealth] = useState(null);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [since, setSince] = useState("");
  const [preview, setPreview] = useState(null); // {id, html}
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (search.trim()) params.q = search.trim();
    if (since) params.since = new Date(since).toISOString();
    const [h, q] = await Promise.all([
      api.get("/admin/emails/queue/health"),
      api.get("/admin/emails/queue", { params }),
    ]);
    setHealth(h.data);
    setRows(q.data);
  }, [status, search, since]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000); // 30s auto-refresh
    return () => clearInterval(t);
  }, [load]);

  const openPreview = async (id) => {
    setBusyId(id);
    try {
      const r = await api.get(`/admin/emails/queue/${id}/preview`, { responseType: "text" });
      setPreview({ id, html: r.data });
    } catch (e) { toast.error("تعذّر جلب المعاينة"); } finally { setBusyId(null); }
  };
  const retry = async (id) => {
    setBusyId(id);
    try { await api.post(`/admin/emails/queue/${id}/retry`); toast.success("سيُعاد إرسالها"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "فشل"); } finally { setBusyId(null); }
  };
  const cancel = async (id) => {
    if (!window.confirm("إلغاء هذه الرسالة المجدولة؟")) return;
    setBusyId(id);
    try { await api.post(`/admin/emails/queue/${id}/cancel`); toast.success("أُلغيت"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "فشل"); } finally { setBusyId(null); }
  };

  const exportCsv = () => {
    const cols = ["scheduled_at","status","to","template_key","sequence_name","step","campaign","sent_at","attribution_token"];
    const lines = [cols.join(",")].concat(rows.map(r => cols.map(c => JSON.stringify(r[c] ?? "")).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `email_queue_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  const totalShown = useMemo(() => rows.length, [rows]);

  return (
    <div className="space-y-4" dir="rtl" data-testid="admin-emails-queue">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold">سجل البريد</h1>
          <p className="text-foreground/60 mt-1 text-sm">صحة النظام + كل الرسائل مع بحث، فلترة، معاينة، إعادة إرسال.</p>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={load} data-testid="queue-refresh"><RefreshCw className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="queue-export"><Download className="h-4 w-4 me-1" />CSV</Button>
        </div>
      </div>

      {/* Health pills */}
      {health && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="queue-health">
          <HealthPill label="قيد الانتظار" value={health.pending} color="yellow" />
          <HealthPill label="أُرسلت (24h)" value={health.sent_24h} color="green" />
          <HealthPill label="فشلت (24h)" value={health.failed_24h} color="red" />
          <HealthPill label="تم التخطي (24h)" value={health.skipped_24h} color="slate" />
          <HealthPill label="معدل النجاح" value={`${health.success_rate_pct}%`} color={health.alert ? "red" : "green"} />
        </div>
      )}
      {health?.alert && (
        <div className="rounded-md bg-red-50 border border-red-200 text-red-800 text-xs p-3 flex items-center gap-2" data-testid="queue-alert">
          <AlertTriangle className="h-4 w-4" />
          معدل الفشل مرتفع في آخر 24 ساعة — راجع الرسائل الفاشلة أدناه.
        </div>
      )}

      {/* Filters */}
      <Card className="p-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="بحث بالبريد أو اسم القالب..."
            className="ps-8 bg-white border-foreground/10 text-foreground"
            data-testid="queue-search"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="border rounded px-2 py-1.5 text-xs bg-white h-9"
          data-testid="queue-status-filter"
        >
          <option value="all">كل الحالات</option>
          <option value="pending">قيد الانتظار</option>
          <option value="sent">أُرسلت</option>
          <option value="failed">فشلت</option>
          <option value="skipped_purchased">اشترى بالفعل</option>
          <option value="skipped_suppressed">قائمة إيقاف</option>
          <option value="cancelled">أُلغيت</option>
        </select>
        <Input
          type="date"
          value={since}
          onChange={(e) => setSince(e.target.value)}
          className="w-40 bg-white border-foreground/10 text-foreground text-xs"
          data-testid="queue-since"
        />
        {(search || status !== "all" || since) && (
          <button onClick={() => { setSearch(""); setStatus("all"); setSince(""); }}
                  className="text-xs text-foreground/60 underline">مسح</button>
        )}
        <span className="text-xs text-foreground/50 ms-auto">{totalShown} نتيجة</span>
      </Card>

      {/* Rows */}
      <div className="space-y-2">
        {rows.length === 0 ? (
          <Card className="p-8 text-center text-foreground/50 text-sm">لا توجد رسائل تطابق الفلاتر.</Card>
        ) : rows.map((r) => (
          <Card key={r.id} className="p-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`queue-row-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Link
                  to={`/admin/journey?email=${encodeURIComponent(r.to)}`}
                  className="font-medium truncate hover:underline"
                  data-testid={`queue-row-to-${r.id}`}
                >{r.to}</Link>
                <ArrowUpRight className="h-3 w-3 text-foreground/40" />
              </div>
              <div className="text-xs text-foreground/50 mt-0.5 truncate">
                {r.template_key}
                {r.sequence_name && <> · {r.sequence_name}</>}
                {r.step && <> · {r.step}</>}
                {r.campaign && <> · {r.campaign}</>}
              </div>
              <div className="text-[10px] text-foreground/40 mt-0.5">
                مجدولة: {new Date(r.scheduled_at).toLocaleString("ar-SA")}
                {r.sent_at && <> · أُرسلت: {new Date(r.sent_at).toLocaleString("ar-SA")}</>}
                {r.attribution_token && <> · 🎯 مُتتبَّعة</>}
              </div>
            </div>
            <StatusChip status={r.status} />
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" disabled={busyId === r.id} onClick={() => openPreview(r.id)} title="معاينة" data-testid={`queue-preview-${r.id}`}>
                <Eye className="h-3.5 w-3.5" />
              </Button>
              {r.status === "failed" && (
                <Button size="sm" variant="outline" disabled={busyId === r.id} onClick={() => retry(r.id)} title="إعادة الإرسال" data-testid={`queue-retry-${r.id}`}>
                  <RotateCcw className="h-3.5 w-3.5" />
                </Button>
              )}
              {r.status === "pending" && (
                <Button size="sm" variant="outline" disabled={busyId === r.id} onClick={() => cancel(r.id)} title="إلغاء" className="text-red-700" data-testid={`queue-cancel-${r.id}`}>
                  <XCircle className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Preview drawer */}
      <Sheet open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <SheetContent side="left" className="w-full sm:max-w-2xl overflow-y-auto p-0">
          <SheetHeader className="p-4 border-b"><SheetTitle>معاينة الرسالة</SheetTitle></SheetHeader>
          <iframe title="preview" srcDoc={preview?.html || ""} className="w-full h-[85vh] bg-white" data-testid="queue-preview-iframe" />
        </SheetContent>
      </Sheet>
    </div>
  );
}

function HealthPill({ label, value, color }) {
  const cls = {
    yellow: "bg-yellow-50 border-yellow-200 text-yellow-900",
    green: "bg-green-50 border-green-200 text-green-800",
    red: "bg-red-50 border-red-200 text-red-800",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  }[color] || "bg-white border-foreground/10";
  return (
    <Card className={`p-3 border ${cls}`}>
      <div className="text-[10px] opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
    </Card>
  );
}

function StatusChip({ status }) {
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_CHIPS[status] || "bg-foreground/10"}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}
